/**
 * Notifications Repository
 *
 * Handles CRUD operations and real-time listeners for user notifications
 * stored at users/{uid}/user_notifications/{id}
 *
 * Replaces the previous localStorage-based notification system with
 * Firestore persistence for cross-device sync.
 */

import {
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  query,
  orderBy,
  limit as fbLimit,
  updateDoc,
  setDoc,
  getDocs,
  where,
  writeBatch,
} from 'firebase/firestore';
import { Paths, withBackoff, db } from './firestoreUtils';
import { v4 as uuidv4 } from 'uuid';
import type { FirestoreNotification } from './notificationRules';

// ============================================================================
// Types
// ============================================================================

export {
  BELL_ROWS,
  countAccountLevelUnread,
  isThreadScopedNotification,
  notificationThreadLink,
  notificationSigninLink,
} from './notificationRules';
export type { FirestoreNotification } from './notificationRules';

type NotificationInput = {
  title: string;
  message: string;
  metadata?: Record<string, any>;
};

// ============================================================================
// Real-time Subscription
// ============================================================================

/**
 * Subscribe to real-time notification updates from Firestore.
 * Returns an unsubscribe function.
 */
export function subscribeNotifications(
  uid: string,
  onData: (notifications: FirestoreNotification[]) => void,
  onError?: (error: Error) => void,
  maxItems: number = 100,
): Unsubscribe {
  const col = Paths.userNotificationsCol(uid);
  const q = query(col, orderBy('created_at', 'desc'), fbLimit(maxItems));

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications: FirestoreNotification[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        let timestamp: string;
        if (data.created_at?.toDate) {
          timestamp = data.created_at.toDate().toISOString();
        } else if (data.timestamp) {
          timestamp = data.timestamp;
        } else {
          timestamp = new Date().toISOString();
        }
        return {
          id: doc.id,
          title: data.title || 'notification',
          message: data.message || '',
          timestamp,
          read: data.read === true,
          metadata: data.metadata || undefined,
        };
      });
      onData(notifications);
    },
    (error) => {
      console.error('[notificationsRepository] Snapshot error:', error);
      if (onError) onError(error);
    },
  );
}

// ============================================================================
// Write Operations
// ============================================================================

/**
 * Mark a single notification as read.
 */
export async function markNotificationRead(uid: string, notificationId: string): Promise<void> {
  const ref = Paths.userNotificationDoc(uid, notificationId);
  await withBackoff(() => updateDoc(ref, { read: true }));
}

/**
 * Mark all unread notifications as read (batch write).
 */
export async function markAllNotificationsRead(uid: string): Promise<void> {
  const col = Paths.userNotificationsCol(uid);
  const q = query(col, where('read', '==', false));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return;

  const batch = writeBatch(db);
  snapshot.docs.forEach((doc) => {
    batch.update(doc.ref, { read: true });
  });
  await batch.commit();
}

/**
 * Create a new notification in Firestore.
 */
export async function createNotification(uid: string, data: NotificationInput): Promise<string> {
  const id = uuidv4();
  const ref = Paths.userNotificationDoc(uid, id);
  await withBackoff(() =>
    setDoc(ref, {
      title: data.title,
      message: data.message,
      created_at: serverTimestamp(),
      read: false,
      metadata: data.metadata || null,
    }),
  );
  return id;
}

// ============================================================================
// Migration: localStorage → Firestore (one-time)
// ============================================================================

const MIGRATION_FLAG_PREFIX = 'notifications_migrated_to_firestore_';

/**
 * Migrate existing localStorage notifications to Firestore.
 * Runs once per account, then sets a flag so it won't run again.
 */
export async function migrateLocalStorageNotifications(uid: string, accountId: string): Promise<void> {
  const migrationKey = `${MIGRATION_FLAG_PREFIX}${accountId}`;
  if (localStorage.getItem(migrationKey)) return;

  try {
    const raw = localStorage.getItem(`notifications_${accountId}`);
    if (!raw) {
      localStorage.setItem(migrationKey, '1');
      return;
    }

    const items: Array<{ id: string; title: string; message: string; timestamp: string }> = JSON.parse(raw);
    if (!Array.isArray(items) || items.length === 0) {
      localStorage.setItem(migrationKey, '1');
      return;
    }

    const readRaw = localStorage.getItem(`notifications_read_${accountId}`);
    const readIds: string[] = readRaw ? JSON.parse(readRaw) : [];

    // Write each notification to Firestore
    const batch = writeBatch(db);
    for (const item of items.slice(0, 100)) {
      const ref = Paths.userNotificationDoc(uid, item.id || uuidv4());
      batch.set(ref, {
        title: item.title || 'notification',
        message: item.message || '',
        created_at: item.timestamp ? new Date(item.timestamp) : new Date(),
        read: readIds.includes(item.id),
        metadata: null,
      });
    }
    await batch.commit();

    // Clear localStorage keys
    localStorage.removeItem(`notifications_${accountId}`);
    localStorage.removeItem(`notifications_read_${accountId}`);
    localStorage.setItem(migrationKey, '1');
  } catch (err) {
    console.warn('[notificationsRepository] Migration failed:', err);
  }
}
